import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ShowCard from '../components/ShowCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, User, Heart, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const AVATAR_COLORS = ['#FF3860', '#4AC6FF', '#FFDE00', '#39FF14', '#FF6B00', '#FF007F', '#9B59B6', '#1ABC9C'];

export default function KidProfilesPage() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [profileFavorites, setProfileFavorites] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [form, setForm] = useState({ display_nickname: '', avatar_color: '#FF3860' });

  const fetchProfiles = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await ax.get(`${API}/profiles`);
      setProfiles(data);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const fetchFavorites = useCallback(async () => {
    if (!activeProfile) return;
    try {
      const { data } = await ax.get(`${API}/profiles/${activeProfile.id}/favorites`);
      setProfileFavorites(data);
    } catch { /* ignore */ }
  }, [activeProfile]);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  const openCreate = () => { setEditProfile(null); setForm({ display_nickname: '', avatar_color: AVATAR_COLORS[profiles.length % AVATAR_COLORS.length] }); setDialogOpen(true); };
  const openEdit = (p) => { setEditProfile(p); setForm({ display_nickname: p.display_nickname || '', avatar_color: p.avatar_color }); setDialogOpen(true); };

  const handleSave = async () => {
    const nick = (form.display_nickname || '').trim();
    if (!nick) {
      toast.error('Please enter a nickname (not your child\'s real name)');
      return;
    }
    try {
      const payload = { display_nickname: nick, avatar_color: form.avatar_color };
      if (editProfile) {
        await ax.put(`${API}/profiles/${editProfile.id}`, payload);
        toast.success('Profile updated');
      } else {
        await ax.post(`${API}/profiles`, payload);
        toast.success('Profile created!');
      }
      setDialogOpen(false);
      fetchProfiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this profile?')) return;
    try {
      await ax.delete(`${API}/profiles/${id}`);
      toast.success('Profile deleted');
      if (activeProfile?.id === id) setActiveProfile(null);
      fetchProfiles();
    } catch { toast.error('Failed to delete'); }
  };

  if (!user) return (
    <div className="page-content min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <User size={48} className="text-[#FF3860] mx-auto mb-4" strokeWidth={1.5} />
        <p className="font-['Fredoka'] text-2xl text-[#0A0A0A] mb-2">Sign in to manage profiles</p>
        <Link to="/login" className="neo-btn inline-block px-6 py-2 rounded-xl bg-[#FF3860] text-white text-sm">Sign In</Link>
      </div>
    </div>
  );

  return (
    <div className="page-content" data-testid="kid-profiles-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Fredoka'] text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight" data-testid="profiles-title">
              Kid Profiles
            </h1>
            <p className="text-base text-[#4B5563] font-['Nunito'] mt-1">Each child gets their own favorites and watch list</p>
          </div>
          {profiles.length < 4 && (
            <button onClick={openCreate} className="neo-btn flex items-center gap-1 px-4 py-2 rounded-xl bg-[#FF3860] text-white text-sm" data-testid="create-profile-btn">
              <Plus size={16} strokeWidth={2.5} /> New Profile
            </button>
          )}
        </div>

        {/* Profile Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {profiles.map(p => (
            <div key={p.id} className="relative" data-testid={`profile-${p.id}`}>
              <button
                onClick={() => setActiveProfile(activeProfile?.id === p.id ? null : p)}
                className={`neo-card w-full p-5 text-center transition-all ${activeProfile?.id === p.id ? 'ring-4 ring-[#FF3860] translate-y-[-2px]' : ''}`}
                style={{ backgroundColor: p.avatar_color + '15' }}
              >
                <div className="w-16 h-16 rounded-full mx-auto mb-3 border-3 border-[#0A0A0A] flex items-center justify-center text-white text-2xl font-['Fredoka'] font-black" style={{ backgroundColor: p.avatar_color, borderWidth: 3 }}>
                  {(p.display_nickname || '?').charAt(0).toUpperCase()}
                </div>
                <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A]">{p.display_nickname}</h3>
              </button>
              <div className="absolute top-2 right-2 flex gap-1">
                <button onClick={() => openEdit(p)} className="p-1 rounded bg-white/80 hover:bg-white border border-[#0A0A0A]/20" data-testid={`edit-profile-${p.id}`}>
                  <Pencil size={12} />
                </button>
                <button onClick={() => handleDelete(p.id)} className="p-1 rounded bg-white/80 hover:bg-white border border-[#0A0A0A]/20 text-[#FF007F]" data-testid={`delete-profile-${p.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
          {profiles.length === 0 && (
            <div className="col-span-4 neo-card p-8 text-center bg-white">
              <User size={40} className="text-[#4B5563] mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-['Fredoka'] text-lg text-[#4B5563] mb-3">No profiles yet</p>
              <button onClick={openCreate} className="neo-btn px-4 py-2 rounded-xl bg-[#FF3860] text-white text-sm">Create First Profile</button>
            </div>
          )}
        </div>

        {/* Active Profile Favorites */}
        {activeProfile && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Heart size={20} strokeWidth={2.5} className="text-[#FF3860]" />
              <h2 className="font-['Fredoka'] text-2xl font-bold text-[#0A0A0A]">{activeProfile.display_nickname}&apos;s Favorites</h2>
            </div>
            {profileFavorites.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {profileFavorites.map((show, i) => <ShowCard key={show.id} show={show} index={i} />)}
              </div>
            ) : (
              <div className="neo-card p-6 text-center bg-white">
                <p className="text-[#4B5563] font-['Nunito']">No favorites for {activeProfile.display_nickname} yet. Browse shows and add some!</p>
                <Link to="/browse" className="neo-btn inline-block mt-3 px-4 py-2 rounded-xl bg-[#FF3860] text-white text-sm">
                  Browse Shows <ChevronRight size={14} strokeWidth={2.5} className="inline" />
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] max-w-sm" data-testid="profile-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Fredoka'] text-xl">{editProfile ? 'Edit' : 'New'} Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">
                  Nickname or display name <span className="text-[#FF3860]">*</span>
                </label>
                <p className="text-xs text-[#4B5563] font-['Nunito'] mb-2">
                  Do <strong>not</strong> use your child&apos;s real name. Pick a fun nickname, initials, or a favorite character.
                </p>
                <input
                  type="text"
                  value={form.display_nickname}
                  onChange={(e) => setForm(f => ({...f, display_nickname: e.target.value}))}
                  placeholder="e.g. Sparky, Little Chef, K.J."
                  maxLength={30}
                  required
                  className="w-full px-3 py-2 border-2 border-[#0A0A0A] rounded-lg text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
                  data-testid="profile-nickname-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {AVATAR_COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setForm(f => ({...f, avatar_color: c}))}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${form.avatar_color === c ? 'border-[#0A0A0A] scale-125 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setDialogOpen(false)} className="neo-btn px-4 py-2 rounded-lg bg-white text-sm">Cancel</button>
              <button onClick={handleSave} className="neo-btn px-4 py-2 rounded-lg bg-[#FF3860] text-white text-sm" data-testid="save-profile-btn">
                {editProfile ? 'Update' : 'Create'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
